import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useCart } from '../contexts/CartContext';

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const query = searchParams.get('q') || '';
  const cityId = searchParams.get('cityId');
  const garageSellerId = searchParams.get('garageSellerId');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        let url = `/api/search?q=${encodeURIComponent(query)}`;
        if (cityId) url += `&cityId=${cityId}`;
        if (garageSellerId) url += `&garageSellerId=${garageSellerId}`;
        
        const res = await api(url, {});
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setResults(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [query, cityId, garageSellerId]);

  const handleItemClick = (itemId) => {
    navigate(`/item/${itemId}`);
  };

  const handleAddToCart = (e, itemId, isOutOfStock) => {
    e.stopPropagation();
    if (!isOutOfStock) {
      addItem(itemId, 1);
    }
  };

  // Group results by city
  const groupedByCity = results.reduce((acc, item) => {
    const cityKey = item.marketId || 'unknown';
    if (!acc[cityKey]) {
      acc[cityKey] = {
        cityId: item.marketId,
        cityName: item.marketName || 'Unknown City',
        items: []
      };
    }
    acc[cityKey].items.push(item);
    return acc;
  }, {});

  // Sort cities and items within cities
  const cityGroups = Object.values(groupedByCity).map(group => ({
    ...group,
    items: group.items.sort((a, b) => {
      // Sort by price (ascending), then by garage seller name
      if (a.price !== b.price) {
        return a.price - b.price;
      }
      return (a.vendorName || '').localeCompare(b.vendorName || '');
    })
  }));

  const clearFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('cityId');
    newParams.delete('garageSellerId');
    navigate(`/search?${newParams.toString()}`);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-semibold">Search Results</h1>
        {(cityId || garageSellerId) && (
          <button
            onClick={clearFilters}
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {(cityId || garageSellerId) && (
        <div className="flex flex-wrap gap-2">
          {cityId && (
            <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 rounded-full text-sm font-medium">
              Filtered by: City
            </span>
          )}
          {garageSellerId && (
            <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 rounded-full text-sm font-medium">
              Filtered by: Garage Sale
            </span>
          )}
        </div>
      )}
      
      {!query.trim() ? (
        <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
          <p className="text-xl text-slate-600 dark:text-slate-400">
            Enter a search term to find items.
          </p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-2xl text-slate-500 dark:text-slate-400">Searching…</p>
          </div>
        </div>
      ) : error ? (
        <div className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
          {error}
        </div>
      ) : results.length === 0 ? (
        <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
          <p className="text-xl text-slate-600 dark:text-slate-400">
            No items found for "{query}".
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {cityGroups.map((group) => (
            <div key={group.cityId || 'unknown'} className="flex flex-col gap-4">
              <h2 className="text-2xl font-semibold border-b-2 border-indigo-600 dark:border-indigo-400 pb-2">
                {group.cityName} ({group.items.length} {group.items.length === 1 ? 'item' : 'items'})
              </h2>
              
              <div className="flex flex-col gap-0">
                {group.items.map((item) => {
                  const imageUrls = item.imageUrls || [];
                  const firstImage = imageUrls.length > 0 ? imageUrls[0] : null;
                  const isOutOfStock = item.stockCount <= 0;
                  const hasLowStock = item.stockCount > 0 && item.stockCount < 10;
                  
                  return (
                    <div
                      key={item.id}
                      onClick={() => handleItemClick(item.id)}
                      className="relative w-full bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      {/* Amazon-style horizontal card layout */}
                      <div className="flex gap-3 p-3">
                        {/* Left: Image - Only show first image, no carousel controls */}
                        <div className="relative flex-shrink-0 w-32 h-32 bg-slate-100 dark:bg-slate-900 rounded">
                          {firstImage ? (
                            <>
                              <img
                                src={firstImage}
                                alt={item.title}
                                className="w-full h-full object-contain rounded"
                              />
                              {/* Overlay icon (like Amazon's share/list icon) */}
                              <div className="absolute bottom-1 left-1 w-6 h-6 bg-white/90 rounded flex items-center justify-center">
                                <svg className="w-4 h-4 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                              No image
                            </div>
                          )}
                        </div>

                        {/* Right: Details */}
                        <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                          {/* Title */}
                          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 line-clamp-2 leading-tight">
                            {item.title}
                          </h3>

                          {/* Rating (placeholder - you can add actual ratings later) */}
                          <div className="flex items-center gap-1">
                            <div className="flex items-center">
                              {Array.from({ length: 5 }, (_, i) => (
                                <span
                                  key={i}
                                  className={`text-xs ${i < 4 ? 'text-orange-400' : 'text-slate-300 dark:text-slate-600'}`}
                                >
                                  ★
                                </span>
                              ))}
                            </div>
                            <span className="text-xs text-slate-600 dark:text-slate-400">(0)</span>
                          </div>

                          {/* Price */}
                          <div className="flex items-baseline gap-1">
                            <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                              ${parseFloat(item.price).toFixed(2).split('.')[0]}
                            </span>
                            <span className="text-xs text-slate-600 dark:text-slate-400 align-super">
                              {parseFloat(item.price).toFixed(2).split('.')[1]}
                            </span>
                          </div>

                          {/* Stock Warning */}
                          {hasLowStock && !isOutOfStock && (
                            <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                              Only {item.stockCount} left in stock - order soon.
                            </p>
                          )}
                          {isOutOfStock && (
                            <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                              Out of stock
                            </p>
                          )}

                          {/* Garage seller info (smaller text) */}
                          {item.vendorName && (
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              Garage Sale: <span className="font-medium">{item.vendorName} Garage sale</span>
                              {item.vendorLocation && ` (${item.vendorLocation})`}
                            </p>
                          )}

                          {/* Add to Cart Button */}
                          <button
                            type="button"
                            onClick={(e) => handleAddToCart(e, item.id, isOutOfStock)}
                            disabled={isOutOfStock}
                            className="mt-1 w-full h-9 rounded-md bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors font-semibold text-sm text-slate-900 flex items-center justify-center"
                          >
                            {isOutOfStock ? 'Out of Stock' : 'Add to cart'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
